import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { toSearchParams } from "helpers/form"
import { toaster } from "components/common/Toast"
import { buildGuestTypes } from "pagesV2/pro/Bookings/CheckIn/checkInUtils"

const PAGINATION_INITIAL_STATE = {
  page: 1,
  next_page: null,
  prev_page: null,
  total_results: 0,
  total_pages: 1
}

// ACTIONS
const requested = createAction("upcomingExperiences/requested")
const received = createAction("upcomingExperiences/received")
const failed = createAction("upcomingExperiences/failed")

const requestedActionQueue = createAction("upcomingExperiences/actionQueue/requested")
const receivedActionQueue = createAction("upcomingExperiences/actionQueue/received")
const failedActionQueue = createAction("upcomingExperiences/actionQueue/failed")

const requestedFilters = createAction("upcomingExperiences/filters/requested")
const receivedFilters = createAction("upcomingExperiences/filters/received")
const failedFilters = createAction("upcomingExperiences/filters/failed")

const requestedDetail = createAction("upcomingExperiences/detail/requested")
const receivedDetail = createAction("upcomingExperiences/detail/received")
const failedDetail = createAction("upcomingExperiences/detail/failed")

const requestedNote = createAction("upcomingExperiences/note/requested")
const receivedNote = createAction("upcomingExperiences/note/received")
const failedNote = createAction("upcomingExperiences/note/failed")

const requestedCheckIn = createAction("upcomingExperiences/checkIn/requested")
const receivedCheckIn = createAction("upcomingExperiences/checkIn/received")
const failedCheckIn = createAction("upcomingExperiences/checkIn/failed")

// MAPPER — backend snake_case → FE camelCase for upcoming experience rows
export const mapUpcomingExperienceItem = (item) => {
  const isMultiDay = item.duration_type === "multi_day"
  const timeDisplay = isMultiDay
    ? item.experience_time?.start_time || ""
    : [item.experience_time?.start_time, item.experience_time?.end_time].filter(Boolean).join(" - ")

  return {
    uuid: item.uuid,
    // Booking-level uuid the crew/resource assignment endpoints key off (distinct from row uuid)
    tripBookingUuid: item.trip_booking_uuid || item.uuid,
    groupId: item.group_id || item.uuid,
    bookingId: item.booking_id,
    tripTitle: item.trip_title || "",
    experienceDate: item.experience_date || "",
    experienceTime: timeDisplay,
    durationType: item.duration_type,
    totalGuestCount: item.guest_count || 0,
    paymentStatus: item.payment_status,
    totalAmount: item.total_amount || 0,
    isWaiverAttached: item.is_waiver_attached || false,
    signedWaiverCount: item.signed_waiver_count || 0,
    experienceIsGroup: item.experience_is_group ?? false,
    checkInState: item.check_in_state || "pending",
    checkedInCount: item.checked_in_count ?? 0,
    waiversComplete: (item.docs_signed ?? 0) >= (item.docs_total ?? 0),
    paymentTag: item.payment_tag || { label: "Payment Failed", color: "red" },
    collectionTag: item.collection_tag || null,
    docsSign: item.docs_signed ?? 0,
    docsTotal: item.docs_total ?? 0,
    crew: Array.isArray(item.crew) ? item.crew.map(mapCrewMember) : [],
    resources: Array.isArray(item.resources) ? item.resources : []
  }
}

// Guest group (reservation row) → Reservations table row shape
const mapGuestToReservation = (guest) => ({
  id: guest.id,
  // reservation uuid — target of the check_in PATCH (some payloads expose it as `id`)
  uuid: guest.uuid || guest.id || "",
  bookingId: guest.booking_id ? `#${guest.booking_id}` : "",
  name: guest.primary_guest_name || "",
  phone: guest.phone || "",
  email: guest.email || "",
  // Client identity for the messaging modal (ClientContactForm sends by numeric user_id)
  userId: guest.user_id ?? null,
  user: guest.user || null,
  // Guide-client id — links the client name to the client detail page (/guide/clients/{id})
  guideClientId: guest.guide_client_id,
  guestCount: guest.guest_count ?? 0,
  checkInState: guest.check_in_state || "pending",
  checkedInCount: guest.checked_in_count ?? 0,
  checkedOutCount: guest.checked_out_count ?? 0,
  // Per-type breakdown for the check-in/out modals (falls back to a single "Guests" row)
  guestTypes: buildGuestTypes(guest.guest_types, guest.guest_count ?? 0, guest.checked_in_count ?? 0, guest.checked_out_count ?? 0),
  paymentTag: guest.payment_tag || null,
  collectionTag: guest.collection_tag || null,
  // Waiver/docs badge uses the reservation-level waiver counts. The backend sends these as
  // signed_waivers_count / waivers_count on some payloads and docs_signed / docs_total on
  // others (see reservations.js's equivalent fallback) — try both.
  docsSign: guest.docs_signed ?? 0,
  docsTotal: guest.docs_total ?? 0
})

// Assigned crew carry the ids the unassign endpoint needs (assignment_id + profile_id).
// Both the list and detail responses use the same crew serializer, so both map through here —
// without it, table chips have no assignmentId and the × can't fire the unassign call.
export const mapCrewMember = (member) => ({
  id: member.id,
  name: member.name || "",
  role: member.role_label || member.role || "",
  assignmentId: member.assignment_id ?? member.id,
  profileId: member.profile_id
})

export const mapDetailResource = (resource) => ({
  id: resource.id,
  name: resource.name || "",
  equipmentId: resource.equipment_id,
  assetId: resource.asset_id
})

// MAPPER — detail page (§5): base row fields + capacity/notes/guests
export const mapUpcomingExperienceDetail = (item) => {
  if (!item) return null
  const base = mapUpcomingExperienceItem(item)
  const capacity = item.capacity_summary || {}
  const payment = item.payment_summary || {}
  // guest_groups = one row per reservation
  const guests = Array.isArray(item.guest_groups) ? item.guest_groups : []
  return {
    ...base,
    crew: Array.isArray(item.crew) ? item.crew.map(mapCrewMember) : [],
    resources: Array.isArray(item.resources) ? item.resources.map(mapDetailResource) : [],
    // Detail response exposes group_id (uuid) + numeric booking_id but no trip_booking_uuid;
    // group_id is the booking identifier the assignment endpoints accept for private experiences.
    bookingUuid: item.trip_booking_uuid || item.uuid || item.group_id || "",
    experienceTitle: item.trip_title || item.experience?.title || "",
    experienceType: item.experience_type || "",
    address: item.address || item.experience?.address || "",
    bookingStatus: item.booking_status || "",
    experienceTimeRange: item.experience_time || null,
    capacitySummary: {
      percent: capacity.percent ?? 0,
      guestsBooked: capacity.guests_booked ?? 0,
      capacity: capacity.capacity ?? 0,
      reservationCount: capacity.reservation_count ?? 0
    },
    paymentSummary: {
      received: payment.received ?? 0,
      outstanding: payment.outstanding ?? 0,
      totalBooked: payment.total_booked ?? 0,
      potentialRevenue: payment.potential_additional_revenue ?? 0
    },
    notes: Array.isArray(item.notes) ? item.notes : [],
    guests,
    reservations: guests.map(mapGuestToReservation)
  }
}

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  metadata: { ...PAGINATION_INITIAL_STATE },
  actionQueues: {},
  actionQueueLoading: {},
  experienceFilters: [],
  filters: {
    ...LOADING_INITIAL_STATE
  },
  detail: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  noteOperation: {
    ...LOADING_INITIAL_STATE
  }
}

const upcomingExperiencesReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    const incoming = Array.isArray(action.payload?.items) ? action.payload.items : []
    state.items = (action.props?.append ? state.items : []).concat(incoming.map(mapUpcomingExperienceItem))
    Object.assign(state.metadata, action.payload?.metadata || {})
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [requestedActionQueue.type]: (state, action) => {
    state.actionQueueLoading[action.props?.groupId] = true
  },
  [receivedActionQueue.type]: (state, action) => {
    const groupId = action.props?.groupId
    if (groupId) {
      state.actionQueues[groupId] = action.payload?.guest_groups || []
      state.actionQueueLoading[groupId] = false
    }
  },
  [failedActionQueue.type]: (state, action) => {
    const groupId = action.props?.groupId
    if (groupId) state.actionQueueLoading[groupId] = false
  },
  [requestedFilters.type]: (state) => {
    setRequested(state.filters)
  },
  [receivedFilters.type]: (state, action) => {
    setReceived(state.filters)
    state.experienceFilters = action.payload?.data?.experiences || []
  },
  [failedFilters.type]: (state) => {
    setFailed(state.filters)
  },
  [requestedDetail.type]: (state) => {
    setRequested(state.detail)
    state.detail.data = null
  },
  [receivedDetail.type]: (state, action) => {
    setReceived(state.detail)
    state.detail.data = mapUpcomingExperienceDetail(action.payload?.data || action.payload)
  },
  [failedDetail.type]: (state) => {
    setFailed(state.detail)
  },
  [requestedNote.type]: (state) => {
    setRequested(state.noteOperation)
  },
  [receivedNote.type]: (state, action) => {
    setReceived(state.noteOperation)
    if (action.payload?.message) toaster.success({ text: action.payload.message })
  },
  [failedNote.type]: (state) => {
    setFailed(state.noteOperation)
  },
  [requestedCheckIn.type]: () => {},
  [receivedCheckIn.type]: (state, action) => {
    const updated = action.payload?.data
    if (!updated?.uuid) return
    const index = state.items.findIndex((item) => item.uuid === updated.uuid)
    if (index === -1) return
    if (updated.check_in_state) state.items[index].checkInState = updated.check_in_state
    if (updated.checked_in_count != null) state.items[index].checkedInCount = updated.checked_in_count
  },
  [failedCheckIn.type]: () => {}
})

export default upcomingExperiencesReducer

// PUBLIC ACTIONS

export const fetchUpcomingExperiences =
  (params, isAppend = false) =>
  (dispatch) => {
    const serializedParams = toSearchParams(params, null)
    return dispatch(
      api({
        url: "/guide/upcoming_experiences",
        onStart: requested.type,
        onSuccess: received.type,
        onError: failed.type,
        params: serializedParams,
        props: { append: Boolean(isAppend) }
      })
    )
  }

export const fetchActionQueue = (groupId) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/upcoming_experiences/${groupId}/action_queue`,
      onStart: requestedActionQueue.type,
      onSuccess: receivedActionQueue.type,
      onError: failedActionQueue.type,
      props: { groupId }
    })
  )

export const fetchExperienceFilters = () => (dispatch) =>
  dispatch(
    api({
      url: "/guide/upcoming_experiences/filters",
      onStart: requestedFilters.type,
      onSuccess: receivedFilters.type,
      onError: failedFilters.type
    })
  )

// §5 — Upcoming Experience detail page (envelope: { data })
export const fetchUpcomingExperienceDetail = (groupId) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/upcoming_experiences/${groupId}`,
      onStart: requestedDetail.type,
      onSuccess: receivedDetail.type,
      onError: failedDetail.type,
      props: { groupId }
    })
  )

// Notes CRUD — a booking resource mounted under two parents (same controller/payloads):
//   "upcoming_experiences" (upcoming only) or "reservations" (any non-draft booking).
// :id == the booking uuid. Multipart when files are attached or files are being removed.
export const NOTE_PARENTS = {
  upcoming: "upcoming_experiences",
  reservation: "reservations"
}

const notesBase = (bookingId, parent = NOTE_PARENTS.upcoming) => `/guide/${parent}/${bookingId}/notes`

const buildNoteFormData = ({ content, files = [], removeAttachmentIds = [] }) => {
  const form = new FormData()
  if (content != null) form.append("note[content]", content)
  files.forEach((file) => form.append("note[attachments][]", file))
  removeAttachmentIds.forEach((id) => form.append("note[remove_attachment_ids][]", id))
  return form
}

// List is not paginated and returns every note newest-first. No success toast on load.
export const listExperienceNotes = (bookingId, parent = NOTE_PARENTS.upcoming) =>
  api({ url: notesBase(bookingId, parent), method: "get", onError: failedNote.type })

// `onUploadProgress` is an axios progress callback; it only fires for the multipart (with-files)
// variant, so callers should treat "no progress event" as "nothing was uploaded".
export const createExperienceNote = (bookingId, { content, files = [] }, parent = NOTE_PARENTS.upcoming, onUploadProgress) =>
  api({
    url: notesBase(bookingId, parent),
    method: "post",
    data: files.length ? buildNoteFormData({ content, files }) : { note: { content } },
    onUploadProgress: files.length ? onUploadProgress : undefined,
    onStart: requestedNote.type,
    onSuccess: receivedNote.type,
    onError: failedNote.type
  })

export const updateExperienceNote = (
  bookingId,
  noteId,
  { content, files = [], removeAttachmentIds = [] },
  parent = NOTE_PARENTS.upcoming,
  onUploadProgress
) => {
  const isMultipart = files.length > 0 || removeAttachmentIds.length > 0
  return api({
    url: `${notesBase(bookingId, parent)}/${noteId}`,
    method: "patch",
    data: isMultipart ? buildNoteFormData({ content, files, removeAttachmentIds }) : { note: { content } },
    onUploadProgress: files.length ? onUploadProgress : undefined,
    onStart: requestedNote.type,
    onSuccess: receivedNote.type,
    onError: failedNote.type
  })
}

export const deleteExperienceNote = (bookingId, noteId, parent = NOTE_PARENTS.upcoming) =>
  api({
    url: `${notesBase(bookingId, parent)}/${noteId}`,
    method: "delete",
    onStart: requestedNote.type,
    onSuccess: receivedNote.type,
    onError: failedNote.type
  })

// Resend the Stripe payment link for the booking's remaining balance (email/SMS to client).
// Resolves with { payment_link } and shows the API `message` as a success toast.
//
// `reservationUuids` narrows the send to specific reservations in the group — the Action Queue
// passes the rows the current filter is actually showing, so the "(N)" on the button matches what
// goes out. Omitted entirely when empty, which is the original whole-group behaviour: the group
// route must keep working for callers that don't scope it.
export const resendPaymentLink = (groupId, reservationUuids = []) =>
  api({
    url: `/guide/upcoming_experiences/${groupId}/payment_link/resend`,
    method: "post",
    data: reservationUuids.length > 0 ? { reservation_uuids: reservationUuids } : undefined,
    showSuccessNotification: true
  })

// Create any missing waiver records for the trip and send the client a signing link.
// No body; shows the API `message` as a success toast.
export const sendWaiverRequest = (groupId) =>
  api({
    url: `/guide/upcoming_experiences/${groupId}/waivers/request`,
    method: "post",
    showSuccessNotification: true
  })

// Partial check-in for a upcoming experience booking. `guestTypes` is [{ key, checkedInCount }]
// (absolute per type); the aggregate `checked_in_count` is sent alongside for backward compat.
// override_documents bypasses the required-docs gate.
export const checkInUpcomingGuests = (uuid, guestTypes = [], { override = false } = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/upcoming_experiences/${uuid}/check_in`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: {
        guest_types: guestTypes.map((type) => ({ key: type.key, checked_in_count: type.checkedInCount })),
        checked_in_count: guestTypes.reduce((sum, type) => sum + (type.checkedInCount ?? 0), 0),
        override_documents: override
      }
    })
  )

// SELECTORS
export const selectUpcomingExperienceDetail = (state) => state.upcomingExperiences.detail.data
export const selectUpcomingExperienceDetailLoading = (state) => state.upcomingExperiences.detail.loading
export const selectNoteMutationLoading = (state) => state.upcomingExperiences.noteOperation.loading
